import type { DateTimeInputProps } from '@healthnz-prm/fhir-engine-renderer/types'
import { ChangeEvent, useRef, useState } from 'react'
import { DateTimeIcon } from '@hugeicons/core-free-icons'
import { HugeiconsIcon } from '@hugeicons/react'
import dayjs from 'dayjs'
import { Input } from '@/components/ui/input'
import { InputFieldset } from '@/components/fhirEngineClinical/inputFieldset'
import { cn } from '@/lib/utils'

// FHIR dateTime requires seconds and a timezone offset when a time is present.
// The native datetime-local input works in "YYYY-MM-DDTHH:mm" local time, so we
// append ":00" seconds plus the local offset on change and trim them for display.
//
// We can't call showPicker() to open the native picker: it throws a SecurityError
// when the wrapper runs in a cross-origin iframe. Instead we stretch the native
// calendar-picker-indicator over the icon area and make it transparent, so the
// user clicks the native control directly (allowed in cross-origin iframes) while
// our branded icon shows underneath as decoration.
//
export default function DateTimeInput(props: DateTimeInputProps) {
  const { disabled, inputId, onBlur, onChange, onFocus, value } = props
  // We need to track the actual value emitted by the input separate from the FHIR date we pass back up
  // This stops the timezone parsing described above over-eagerly turning a partially typed year into a full date
  const lastEmitted = useRef<{ fhir: string; local: string } | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  // We track our own validity as the native input will emit an empty value when invalid
  const [invalid, setInvalid] = useState(false)

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    const local = event.target.value
    setInvalid(event.target.validity.badInput)
    if (!local) {
      lastEmitted.current = null
      onChange(null)
      return
    }
    const fhir = `${local}:00${dayjs(local).format('Z')}`
    lastEmitted.current = { fhir, local }
    onChange(fhir)
  }

  // Need to check validity on blur because React skips onChange when the emitted value is unchanged (empty -> empty)
  function handleBlur() {
    if (inputRef.current) setInvalid(inputRef.current.validity.badInput)
    onBlur?.()
  }

  function getDisplayValue() {
    if (!value) return ''
    // a value we emitted ourselves is shown exactly as it was typed
    if (value === lastEmitted.current?.fhir) return lastEmitted.current.local
    if (!dayjs(value).isValid()) return ''
    // values from external sources (i.e. initial values) are parsed and formatted into the local format
    return dayjs(value).format('YYYY-MM-DDTHH:mm')
  }

  return (
    <InputFieldset
      {...props}
      errorMessage={invalid ? 'Please enter a valid date and time' : undefined}
    >
      <div className='relative'>
        <Input
          ref={inputRef}
          className='bg-background appearance-none pr-8 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-y-0 [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:my-auto [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:w-8 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0'
          type='datetime-local'
          id={inputId}
          disabled={disabled}
          value={getDisplayValue()}
          onBlur={handleBlur}
          onChange={handleChange}
          onFocus={onFocus}
        />
        <HugeiconsIcon
          icon={DateTimeIcon}
          aria-hidden
          className={cn(
            'pointer-events-none absolute right-2 top-1/2 size-4 -translate-y-1/2',
            disabled && 'opacity-50'
          )}
        />
      </div>
    </InputFieldset>
  )
}
